import { useRef } from 'react'
import clsx from 'clsx'

interface ColorInputProps {
  value?: string
  onChange: (value: string | undefined) => void
  placeholder?: string
  className?: string
}

export function ColorInput({ value, onChange, placeholder = '#444444', className }: ColorInputProps) {
  const colorRef = useRef<HTMLInputElement>(null)

  const isValidHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v)

  const handleTextChange = (text: string) => {
    if (!text) {
      onChange(undefined)
      return
    }
    // Auto-prepend # if user types raw hex
    const normalized = text.startsWith('#') ? text : `#${text}`
    onChange(normalized)
  }

  const handleColorPick = (hex: string) => {
    onChange(hex)
  }

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      {/* Color swatch that opens native picker */}
      <div className="relative flex-shrink-0">
        <button
          type="button"
          className={clsx(
            'w-8 h-8 rounded border border-gray-300 cursor-pointer overflow-hidden',
            'focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary-500'
          )}
          style={value && isValidHex(value)
            ? { backgroundColor: value }
            : undefined
          }
          onClick={() => colorRef.current?.click()}
        >
          {(!value || !isValidHex(value)) && (
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  'linear-gradient(45deg, #ccc 25%, transparent 25%), ' +
                  'linear-gradient(-45deg, #ccc 25%, transparent 25%), ' +
                  'linear-gradient(45deg, transparent 75%, #ccc 75%), ' +
                  'linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
              }}
            />
          )}
        </button>
        <input
          ref={colorRef}
          type="color"
          className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none"
          value={value && isValidHex(value) ? value : '#000000'}
          onChange={e => handleColorPick(e.target.value)}
        />
      </div>

      {/* Hex text input */}
      <input
        type="text"
        className={clsx(
          'w-full px-3 py-2 border rounded-lg text-sm transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
        )}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={e => handleTextChange(e.target.value)}
      />
    </div>
  )
}
